"use client"

/*
==================================================
CART MOBILE BODY (Версия с авто-скрытием)

Изменения:
- Кнопка исчезает при скролле вниз.
- Кнопка плавно появляется только в самом верху.
- Сохранен стиль из твоей V5: зеленый фон [#0cad03], белые стрелки.
==================================================
*/

import { HttpTypes } from "@medusajs/types"
import { useEffect, useState, useRef } from "react"
import CartStep from "./steps/cart-step"
import AddressStep from "./steps/address-step"
import ShippingStep from "./steps/shipping-step"
import PaymentStep from "./steps/payment-step"
import ReviewStep from "@modules/checkout/components/review"

type Step = "cart" | "address" | "shipping" | "payment" | "review"

type CartMobileBodyProps = {
  step: Step
  cart?: HttpTypes.StoreCart | null
  customer?: HttpTypes.StoreCustomer | null
  shippingOptions?: HttpTypes.StoreCartShippingOption[]
  paymentMethods?: HttpTypes.StorePaymentProvider[]
  onOpenItemsOnly: () => void
  onRegisterAddressController: (
    controller: { submit: () => void; reset?: () => void } | null
  ) => void
  onAddressSubmittingChange: (value: boolean) => void
  onAddressSaved: () => void
}

export default function CartMobileBody({
  step,
  cart,
  customer = null,
  shippingOptions = [],
  paymentMethods = [],
  onOpenItemsOnly,
  onRegisterAddressController,
  onAddressSubmittingChange,
  onAddressSaved,
}: CartMobileBodyProps) {
  const [isVisible, setIsVisible] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Логика плавного появления/исчезновения при скролле
  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement
      // Если отскроллили больше чем на 10px — скрываем
      if (target.scrollTop > 10) {
        setIsVisible(false)
      } else {
        setIsVisible(true)
      }
    }

    const scrollArea = scrollContainerRef.current
    if (scrollArea) {
      scrollArea.addEventListener("scroll", handleScroll)
    }

    return () => {
      if (scrollArea) {
        scrollArea.removeEventListener("scroll", handleScroll)
      }
    }
  }, [step])

  switch (step) {
    case "cart":
      return (
        /* Добавляем ref и overflow для отслеживания скролла */
        <div 
          ref={scrollContainerRef}
          className="relative h-full overflow-y-auto"
        >
          {/* Контейнер для кнопки, который держит её наверху */}
          <div className="sticky top-0 z-30 h-0 w-full">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (typeof window !== "undefined" && window.navigator.vibrate) {
                  window.navigator.vibrate(10)
                }
                onOpenItemsOnly()
              }}
              className={`
                absolute right-4 top-3
                flex h-7 w-7 items-center justify-center
                rounded-md
                bg-[#0cad03]
                border border-white/90
                shadow-[0_4px_4px_rgba(0,0,0,0.2),0_4px_4px_rgba(0,0,0,0.15)]
                
                /* Плавность появления/исчезновения */
                transition-all duration-300 ease-in-out
                ${isVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-90 -translate-y-2 pointer-events-none"}
                
                active:scale-90 active:shadow-sm
              `}
              aria-label="Expand items"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 text-[#ffffff]"
              >
                <path d="M16 3h5v5" />
                <path d="M8 21H3v-5" />
                <path d="M21 3l-8.5 8.5" />
                <path d="M3 21l8.5-8.5" />
              </svg>
            </button>
          </div>

          <CartStep
            cart={cart ?? null}
            modalCart={cart ?? null}
            shippingOptions={shippingOptions}
          />
        </div>
      )

    case "address":
      return (
        <AddressStep
          cart={cart}
          customer={customer}
          onRegisterController={onRegisterAddressController}
          onSubmittingChange={onAddressSubmittingChange}
          onSaved={onAddressSaved}
        />
      )

    case "shipping":
      return (
        <ShippingStep
          cart={cart}
          availableShippingMethods={shippingOptions || []}
        />
      )

    case "payment":
      return (
        <PaymentStep
          cart={cart}
          availablePaymentMethods={paymentMethods || []}
        />
      )

    case "review":
      return <ReviewStep cart={cart} />

    default:
      return null
  }
}